import { type MedplumClient } from '@medplum/core';
import type {
  CareTeam,
  EpisodeOfCare,
  Group,
  Patient,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes';

import {
  EOC_GROUP_EXT,
  EOC_GROUP_REFERENCE_EXT,
  THERAPY_SERVICE_TYPE_SYSTEM,
} from '@unify/fhir-model/constants';
import {
  addParticipantIfMissing,
  getCareTeamPatientRef,
  getPractitionerParticipantRefs,
} from '@unify/fhir-model/resources/care-team';

export function getActiveGroupMemberPatientIds(group: Group): string[] {
  return (group.member ?? [])
    .filter((m) => !m.inactive && m.entity?.reference?.startsWith('Patient/'))
    .map((m) => m.entity!.reference!.replace('Patient/', ''));
}

export type GroupParticipationEndMode =
  /** PRD §10 suspension — EoC onhold, CareTeam stays active. */
  | 'suspend'
  /** Pre-start withdrawal — EoC cancelled. */
  | 'pre-start-withdraw'
  /** Formal discharge — EoC finished, CareTeam inactive. */
  | 'formal-discharge';

export function setEpisodeOfCareGroupRef(
  resource: EpisodeOfCare,
  groupReference: Reference | undefined,
): EpisodeOfCare {
  const others = (resource.extension ?? []).filter(
    (e) => e.url !== EOC_GROUP_EXT && e.url !== EOC_GROUP_REFERENCE_EXT,
  );
  const extension = groupReference
    ? [...others, { url: EOC_GROUP_EXT, valueReference: groupReference }]
    : others;
  return { ...resource, extension: extension.length ? extension : undefined };
}

export function getEpisodeOfCareGroupRef(resource: EpisodeOfCare): Reference | undefined {
  return (
    resource.extension?.find((e) => e.url === EOC_GROUP_EXT)?.valueReference ??
    resource.extension?.find((e) => e.url === EOC_GROUP_REFERENCE_EXT)?.valueReference
  );
}

/**
 * True when `EpisodeOfCare.type[]` carries a Group Therapy service type. Requests are written with
 * `group` and enrollment with `group-therapy`, so both codes count.
 */
function hasGroupTherapyServiceType(eoc: EpisodeOfCare): boolean {
  return !!eoc.type?.some((t) =>
    t.coding?.some(
      (c) =>
        c.system === THERAPY_SERVICE_TYPE_SYSTEM &&
        (c.code === 'group' || c.code === 'group-therapy'),
    ),
  );
}

/** True when EoC is Group Therapy by type code and/or cohort group-reference. */
export function isGroupTherapyEpisodeOfCare(eoc: EpisodeOfCare): boolean {
  return hasGroupTherapyServiceType(eoc) || !!getEpisodeOfCareGroupRef(eoc)?.reference;
}

export async function findExistingGroupEnrollmentEpisodeOfCare(
  medplum: MedplumClient,
  patientId: string,
  groupId: string,
  opts?: {
    includeWaitlist?: boolean;
    includeFinished?: boolean;
    includeCancelled?: boolean;
    statuses?: string[];
  },
): Promise<EpisodeOfCare | undefined> {
  const statusList =
    opts?.statuses ??
    [
      'active',
      'planned',
      'onhold',
      ...(opts?.includeWaitlist ? ['waitlist'] : []),
      ...(opts?.includeFinished ? ['finished'] : []),
      ...(opts?.includeCancelled ? ['cancelled'] : []),
    ];
  const episodes = await medplum.searchResources('EpisodeOfCare', {
    patient: `Patient/${patientId}`,
    status: statusList.join(','),
    _count: '50',
  });
  const groupRefStr = `Group/${groupId}`;
  return episodes.find(
    (eoc) =>
      getEpisodeOfCareGroupRef(eoc)?.reference === groupRefStr && hasGroupTherapyServiceType(eoc),
  );
}

export function createOrUpdateGroupMemberCareTeam(params: {
  existing?: CareTeam;
  patient: Patient;
  organizationId?: string;
  facilitatorRefs: Reference<Practitioner>[];
  primaryFacilitatorRef?: Reference<Practitioner>;
}): CareTeam {
  const { existing, patient, organizationId, facilitatorRefs, primaryFacilitatorRef } = params;
  let careTeam: CareTeam = existing
    ? { ...existing }
    : {
        resourceType: 'CareTeam',
        status: 'active',
        name: `${patient.name?.[0]?.text ?? 'Client'} — Group care team`,
        subject: { reference: `Patient/${patient.id}` },
        participant: [],
      };

  careTeam.subject = { reference: `Patient/${patient.id}` };
  careTeam.category = [
    {
      coding: [
        {
          system: THERAPY_SERVICE_TYPE_SYSTEM,
          code: 'group-therapy',
          display: 'Group Therapy',
        },
      ],
    },
  ];
  if (organizationId) {
    careTeam.managingOrganization = [{ reference: `Organization/${organizationId}` }];
    careTeam.meta = {
      ...careTeam.meta,
      accounts: [{ reference: `Organization/${organizationId}` }],
    } as CareTeam['meta'];
  }

  const primary = primaryFacilitatorRef ?? facilitatorRefs[0];
  if (primary?.reference) {
    careTeam = addParticipantIfMissing(careTeam, primary, 'PPRF');
  }
  for (const ref of facilitatorRefs) {
    if (ref.reference && ref.reference !== primary?.reference) {
      careTeam = addParticipantIfMissing(careTeam, ref, 'SPRF');
    }
  }

  // Safety: never include other patients
  const subjectRef = getCareTeamPatientRef(careTeam)?.reference;
  careTeam.participant = (careTeam.participant ?? []).filter((p) => {
    const memberRef = p.member?.reference ?? '';
    return !memberRef.startsWith('Patient/') || memberRef === subjectRef;
  });

  return careTeam;
}

export function syncGroupProvidersToMemberCareTeam(
  careTeam: CareTeam,
  facilitatorRefs: Reference<Practitioner>[],
  primaryFacilitatorRef?: Reference<Practitioner>,
): CareTeam {
  const patientRef = getCareTeamPatientRef(careTeam);
  if (!patientRef?.reference?.startsWith('Patient/')) {
    throw new Error('CareTeam subject must be a single Patient.');
  }
  const patientId = patientRef.reference.replace('Patient/', '');
  return createOrUpdateGroupMemberCareTeam({
    existing: careTeam,
    patient: { resourceType: 'Patient', id: patientId },
    facilitatorRefs,
    primaryFacilitatorRef,
  });
}

/** Returns true when careTeam includes any Patient other than the subject. */
export function careTeamIncludesOtherPatients(careTeam: CareTeam): boolean {
  const subjectRef = getCareTeamPatientRef(careTeam)?.reference;
  return (careTeam.participant ?? []).some((p) => {
    const ref = p.member?.reference;
    return ref?.startsWith('Patient/') && ref !== subjectRef;
  });
}

export function getCareTeamParticipantPatientIds(careTeam: CareTeam): string[] {
  return (careTeam.participant ?? [])
    .map((p) => p.member?.reference)
    .filter((r): r is string => !!r?.startsWith('Patient/'))
    .map((r) => r.replace('Patient/', ''));
}

export function getCareTeamPractitionerIds(careTeam: CareTeam): string[] {
  return getPractitionerParticipantRefs(careTeam)
    .map((r) => r.reference?.replace(/^Practitioner\//, ''))
    .filter((id): id is string => !!id);
}
